import React, { useEffect, useState } from 'react';
import { Row, Col, Button, message, Space, Popconfirm } from 'antd';
import ListCard from 'Molecules/ListCard';
import HeadingChip from 'Molecules/HeadingChip';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getReasonList } from '../ducks/actions'
import { removeReason, bulkRemoveReasons } from '../ducks/services'
import ActionButton from 'Molecules/ActionButton';
import Search from './components/Search';
import AllRoles from '../../../../routing/config/AllRoles';
import { allowed } from '../../../../routing/config/utils';
import { getListQueryItems } from 'Modules/Application/ducks/actions';
import { useTranslate } from 'Translate';

export default (props) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const reasons = useSelector((state) => state.systems.reasons);
  const meta = useSelector((state) => state.systems.reasonOption);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [searchVal, setSearchVal] = useState(null);
  const [reasonTypes, setReasonTypes] = useState([]);
  const allowedEdit = allowed([AllRoles.SYSTEM.REASON], 'write');
  const allowedDelete = allowed([AllRoles.SYSTEM.REASON], 'delete');
  const globalListQuery = useSelector((state) => state.global.listQueryItems);
  const i18n = useTranslate();
  const { t } = i18n;

  const colName = [
    {
      title: t("General.code"),
      dataIndex: 'code',
      key: 'code',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.desc"),
      dataIndex: 'desc',
      key: 'desc',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("System.reason_type"),
      dataIndex: 'reason_type',
      key: 'reason_type',
      className: 'enable-click',
      sorter: true
    },
    {
      title: t("General.status"),
      dataIndex: 'status',
      key: 'status',
      sorter: true,
      render: (text, record) =>
        <div className='flex align-items-center'>
          <span className={text === "Active" ? "c-success" : "c-danger"}>
            {t(`General.${text?.toLowerCase()}`)}
          </span>
          {
            allowedEdit &&
            <Button type="link" onClick={() => onAction(record.id, text)}>
              {text === "Active" ? `(${t("General.deactivate")})` : `(${t("General.activate")})`}
            </Button>
          }
        </div>
    },
    {
      title: '',
      dataIndex: 'action',
      key: 'action',
      align: 'center',
      render: (text, record) =>
        <ActionButton
          title="reason"
          btnAction1={() => navigate(`/reason/${record.id}`)}
          recordId={record.id}
          onRemove={allowedDelete && onRemove}
          setLoading={props.setLoading}
          loading={props.loading}
        />
    }
  ]

  const addNew = () => navigate('/reason/add');

  const btnList = [
    {
      title: t("System.remove_select_reason"),
      text: t("General.bulk_remove"),
      classes: 'red-btn text-white attendance-success',
      permit: true,
      action: () => onBulkRemove(),
    },
    {
      text: `+ ${t("System.new_reason")}`,
      action: () => addNew(),
    },
  ];

  useEffect(() => {
    if(!globalListQuery.reasonListQuery) {
      dispatch(getReasonList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('reasonListQuery', 1, limit, '', '', null, props.setLoading));
    } else {
      const {page, limit, sortOrder, sortColKey, searchValue} = globalListQuery.reasonListQuery
      dispatch(getReasonList(page, limit, sortOrder, sortColKey, searchValue, props.setLoading));
      setPage(page);
      setLimit(limit);
      setSearchVal(searchValue);
    }
  }, []);

  useEffect(() => {
    if(meta) {
      if('reason_types' in meta) {
        let temp = [];
        meta.reason_types.map((x, index) => {
          if(index === 0) {
            temp.push({
              label: t("General.all_types"),
              value: ''
            });
          }
          temp.push({
            label: t(`General.${x}`),
            value: x
          })
        })
        setReasonTypes(temp);
      }
    }
  }, [meta]);

  const onSelectChange = (newSelectedRowKeys) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const onAction = async (reasonId, status) => {
    props.setLoading(true);

    const payload = {
      reason_id: reasonId,
      status_event: status === 'Active' ? 'deactivate' : 'activate'
    }

    await removeReason(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("System.reason_status_update_success"));
        setTimeout(() => dispatch(getReasonList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('reasonListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onRemove = async (id) => {
    props.setLoading(true);

    const payload = {
      reason_id: id,
      status_event: 'remove'
    }

    await removeReason(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        message.success(t("System.reason_remove_success"));
        setTimeout(() => dispatch(getReasonList(page, limit, '', '', searchVal, props.setLoading)), 500);
        dispatch(getListQueryItems('reasonListQuery', page, limit, '', '', searchVal, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onBulkRemove = async () => {
    props.setLoading(true);

    const payload = {
      reasons: JSON.stringify(selectedRowKeys)
    }

    await bulkRemoveReasons(payload).then(res => {
      props.setLoading(false);
      if (res.data.code === 200) {
        rowSelection.onChange([]);
        message.success(t("System.reason_remove_success"));
        setPage(1);
        setSearchVal(null);
        setTimeout(() => dispatch(getReasonList(1, limit, '', '', null, props.setLoading)), 500);
        dispatch(getListQueryItems('reasonListQuery', 1, limit, '', '', null, props.setLoading))
      }
    }).catch(e => {
      props.setLoading(false);
      message.error(e.message ?? t("General.something_went_wrong"));
    })
  }

  const onTableChange = (pagination, filters, sorter) => {
    setPage(pagination.current);
    setLimit(pagination.pageSize);
    if (sorter.order) {
      dispatch(getReasonList(pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading));
      dispatch(getListQueryItems('reasonListQuery', pagination.current, pagination.pageSize, sorter.order, sorter.columnKey, searchVal, props.setLoading))
    } else {
      dispatch(getReasonList(pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading));
      dispatch(getListQueryItems('reasonListQuery', pagination.current, pagination.pageSize, '', '', searchVal, props.setLoading))
    }
  }

  const onSearch = (search) => {
    setPage(1);
    if (search) {
      let searching = {};
      searching = {
        name: search.name,
        reason_type: search.reason_type?.value
      };
      setSearchVal(searching);
      dispatch(getReasonList(1, limit, '', '', searching, props.setLoading));
      dispatch(getListQueryItems('reasonListQuery', 1, limit, '', '', searching, props.setLoading))
    } else {
      setSearchVal(null);
      dispatch(getReasonList(1, limit, '', '', null, props.setLoading));
      dispatch(getListQueryItems('reasonListQuery', 1, limit, '', '', null, props.setLoading))
    }
  };

  const onClickRow = (record) => {
    return {
      onClick: (e) => {
        if (e.target.closest(".enable-click"))
          navigate(`/reason/${record.id}`)
      },
    };
  };

  return (
    <>
      <Row gutter={[10, 10]}>
        <Col span={24}>
          <HeadingChip title={t("System.reasons")} btnList={allowedEdit ? btnList : null} btnHidden={selectedRowKeys.length <= 0} />
        </Col>
        <Col span={24} className="clickRow">
          <ListCard
            onRow={onClickRow}
            Search={Search}
            onSearch={onSearch}
            searchVal={globalListQuery.reasonListQuery?.searchValue}
            ListData={reasons?.list?.map(x => ({ ...x, key: x.id }))}
            ListCol={colName}
            rowSelection={allowedEdit && rowSelection}
            onChange={onTableChange}
            field1={reasonTypes}
            pagination={{
              total: reasons?.total_count,
              current: page,
              pageSize: limit
            }}
          />
        </Col>
      </Row>
    </>
  )
}